import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import useOutsideClick from "src/hooks/useOutsideClick";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { CASH_TRANSACTIONS_BY_STORE } from "src/constants/queries";
import { CASH_TRANSACTION_CREATE } from "src/constants/mutations";
import { GET_EMPLOYEE } from "src/constants/queries";
import { formatShortDate, formatShortTime } from "src/utils/date";
import { failureToast, successToast } from "src/utils/toasts";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportDateRangePicker } from "src/features/Reports/components/ReportDateRangePicker";
import {
  getReportDateRange,
  type ReportDatePreset,
} from "src/features/Reports/utils/reportDateRange";
import {
  type CashDrawerTransaction,
  type CashDrawerTransactionType,
  type CashDrawerPageInfo,
  CASH_DRAWER_TYPE_LABELS,
  CASH_DRAWER_TYPE_STYLES,
} from "./types";
import { CashDrawerActionModal } from "./components/CashDrawerActionModal";

const BORDER_LIGHT = "rgba(0,0,0,0.06)";

const ADD_TRANSACTION_OPTIONS: { type: CashDrawerTransactionType; title: string }[] = [
  { type: "CASH_DROP", title: "Cash Drop" },
  { type: "CASH_REFILL", title: "Cash Refill" },
  { type: "OPENING_FLOAT", title: "Opening Float" },
];

const PAGE_SIZE = 10;

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function formatAmount(tx: CashDrawerTransaction): { text: string; positive: boolean } {
  const isNegative = tx.transactionType === "CASH_DROP" || tx.amountCents < 0;
  const abs = Math.abs(tx.amountCents);
  const text = (isNegative ? "−" : "+") + formatCents(abs);
  return { text, positive: !isNegative };
}

/** Build page list for pagination. */
function getPaginationPages(
  currentPage: number,
  totalPages: number
): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [
      1,
      "ellipsis",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }
  return [
    1,
    "ellipsis",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "ellipsis",
    totalPages,
  ];
}

interface EmployeeOption {
  correlationId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

function resolveCreatedBy(
  createdByUserCorrelationId: string | null | undefined,
  employeeMap: Map<string, string>
): string {
  if (createdByUserCorrelationId == null || createdByUserCorrelationId === "")
    return "System";
  const name = employeeMap.get(createdByUserCorrelationId);
  if (name) return name;
  return "System";
}

export function CashDrawerPage() {
  const { hasRestaurant, user } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const storeCorrelationId = hasRestaurant?.correlationId;

  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);

  const [currentBalance, setCurrentBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<CashDrawerTransaction[]>([]);
  const [pageInfo, setPageInfo] = useState<CashDrawerPageInfo>({
    startCursor: null,
    endCursor: null,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [cursorCache, setCursorCache] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [actionModal, setActionModal] = useState<{
    type: CashDrawerTransactionType;
    title: string;
  } | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [addTransactionOpen, setAddTransactionOpen] = useState(false);
  const addTransactionRef = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref: addTransactionRef, handler: () => setAddTransactionOpen(false) });

  const dateRange = useMemo(() => {
    const range = getReportDateRange(
      datePreset,
      customFrom ? new Date(customFrom) : undefined,
      customTo ? new Date(customTo) : undefined
    );
    return {
      from: range.from,
      to: range.to,
      startTimeInEpochMilli: new Date(range.from).getTime(),
      endTimeInEpochMilli: new Date(range.to).getTime(),
    };
  }, [datePreset, customFrom, customTo]);

  const employeeMap = useMemo(() => {
    const m = new Map<string, string>();
    employees.forEach((e) => {
      const name =
        [e.firstName, e.lastName].filter(Boolean).join(" ") ||
        e.email ||
        e.correlationId;
      m.set(e.correlationId, name);
    });
    if (user?.storeUser?.correlationId) {
      const name =
        [user.storeUser.firstName, user.storeUser.lastName]
          .filter(Boolean)
          .join(" ") || user.storeUser.email || "You";
      m.set(user.storeUser.correlationId, name);
    }
    return m;
  }, [employees, user]);

  const fetchEmployees = useCallback(async () => {
    if (!storeCorrelationId) return;
    try {
      const { data } = await axBe.post<{
        data?: { storeEmployeesByStoreCorrelationId?: EmployeeOption[] };
      }>(GRAPHQL_ENDPOINT, {
        query: GET_EMPLOYEE,
        variables: { storeCorrelationId },
      });
      setEmployees(data?.data?.storeEmployeesByStoreCorrelationId ?? []);
    } catch {
      setEmployees([]);
    }
  }, [storeCorrelationId, axBe]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const fetchBalance = useCallback(async () => {
    if (!storeCorrelationId) return;
    setBalanceLoading(true);
    try {
      const { data } = await axBe.post<{
        data?: {
          cashTransactionsByStore?: {
            edges?: Array<{ node?: { balanceAfter: number } }>;
          };
        };
        errors?: Array<{ message?: string }>;
      }>(GRAPHQL_ENDPOINT, {
        query: CASH_TRANSACTIONS_BY_STORE,
        variables: {
          storeCorrelationId,
          first: 1,
        },
      });
      const err = data?.errors?.[0];
      if (err) {
        setCurrentBalance(null);
      } else {
        const node = data?.data?.cashTransactionsByStore?.edges?.[0]?.node;
        setCurrentBalance(node?.balanceAfter ?? 0);
      }
    } catch {
      setCurrentBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, [storeCorrelationId, axBe]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const fetchTransactions = useCallback(
    async (cursor: { after?: string | null; before?: string | null }, pageNum: number) => {
      if (!storeCorrelationId) return;
      setLoading(true);
      try {
        const variables: Record<string, unknown> = {
          storeCorrelationId,
          first: cursor?.before != null ? undefined : PAGE_SIZE,
          last: cursor?.before != null ? PAGE_SIZE : undefined,
          after: cursor?.after ?? undefined,
          before: cursor?.before ?? undefined,
          startTimeInEpochMilli: dateRange.startTimeInEpochMilli,
          endTimeInEpochMilli: dateRange.endTimeInEpochMilli,
        };
        const { data } = await axBe.post<{
          data?: {
            cashTransactionsByStore?: {
              pageInfo?: CashDrawerPageInfo;
              edges?: Array<{ cursor: string; node: CashDrawerTransaction }>;
            };
          };
          errors?: Array<{ message?: string }>;
        }>(GRAPHQL_ENDPOINT, {
          query: CASH_TRANSACTIONS_BY_STORE,
          variables,
        });
        const err = data?.errors?.[0];
        if (err) {
          failureToast(err.message ?? "Failed to load cash transactions");
          setTransactions([]);
        } else {
          const conn = data?.data?.cashTransactionsByStore;
          const edges = conn?.edges ?? [];
          setTransactions(edges.map((e) => e.node));
          const pi: Partial<CashDrawerPageInfo> = conn?.pageInfo ?? {};
          const endCursor = pi.endCursor ?? null;
          setPageInfo({
            startCursor: pi.startCursor ?? null,
            endCursor,
            hasPreviousPage: pi.hasPreviousPage ?? false,
            hasNextPage: pi.hasNextPage ?? false,
            totalNumberOfPages: pi.totalNumberOfPages,
            totalNumberOfTransactions: pi.totalNumberOfTransactions,
          });
          if (endCursor != null) {
            setCursorCache((prev) => ({ ...prev, [pageNum + 1]: endCursor }));
          }
        }
      } catch (e) {
        failureToast("Failed to load cash transactions");
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    },
    [storeCorrelationId, dateRange, axBe]
  );

  useEffect(() => {
    setAfter(null);
    setBefore(null);
    setCurrentPage(1);
    setCursorCache({});
  }, [dateRange.startTimeInEpochMilli, dateRange.endTimeInEpochMilli]);

  useEffect(() => {
    if (!storeCorrelationId) return;
    fetchTransactions({ after, before }, currentPage);
  }, [storeCorrelationId, dateRange, after, before, currentPage, fetchTransactions]);

  const totalTransactions = pageInfo.totalNumberOfTransactions ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalTransactions / PAGE_SIZE));
  const start =
    totalTransactions === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end =
    totalTransactions === 0
      ? 0
      : Math.min(currentPage * PAGE_SIZE, totalTransactions);
  const pageNumbers = getPaginationPages(currentPage, totalPages);

  const loadNextPage = useCallback(() => {
    if (loading) return;
    if (pageInfo.hasNextPage && pageInfo.endCursor) {
      setBefore(null);
      setAfter(pageInfo.endCursor);
      setCurrentPage((p) => p + 1);
    }
  }, [loading, pageInfo.hasNextPage, pageInfo.endCursor]);

  const loadPrevPage = useCallback(() => {
    if (loading) return;
    if (pageInfo.hasPreviousPage && pageInfo.startCursor) {
      setAfter(null);
      setBefore(pageInfo.startCursor);
      setCurrentPage((p) => Math.max(1, p - 1));
    }
  }, [loading, pageInfo.hasPreviousPage, pageInfo.startCursor]);

  const goToPage = useCallback(
    (page: number) => {
      if (loading) return;
      const target = Math.max(1, Math.min(page, totalPages));
      if (target === currentPage) return;
      if (target === 1) {
        setAfter(null);
        setBefore(null);
        setCurrentPage(1);
        return;
      }
      const cursor = cursorCache[target];
      if (cursor != null) {
        setAfter(cursor);
        setBefore(null);
        setCurrentPage(target);
      } else if (target === currentPage + 1 && pageInfo.endCursor) {
        loadNextPage();
      }
    },
    [loading, cursorCache, currentPage, totalPages, pageInfo.endCursor, loadNextPage]
  );

  const canGoToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return false;
      if (page === 1) return true;
      if (page === currentPage + 1) return pageInfo.hasNextPage ?? false;
      return cursorCache[page] != null;
    },
    [totalPages, currentPage, cursorCache, pageInfo.hasNextPage]
  );

  const handleDateRangeChange = useCallback(
    (preset: ReportDatePreset, from: string, to: string) => {
      setDatePreset(preset);
      setCustomFrom(preset === "custom" ? from : null);
      setCustomTo(preset === "custom" ? to : null);
    },
    []
  );

  const handleCreateTransaction = useCallback(
    async (amountCents: number, note: string) => {
      if (!storeCorrelationId || !actionModal || !user?.storeUser?.correlationId)
        return;
      setCreateLoading(true);
      try {
        const { data } = await axBe.post<{
          data?: { cashTransactionCreate?: CashDrawerTransaction };
          errors?: Array<{ message?: string }>;
        }>(GRAPHQL_ENDPOINT, {
          query: CASH_TRANSACTION_CREATE,
          variables: {
            input: {
              storeCorrelationId,
              type: actionModal.type,
              amountCents,
              note: note || undefined,
              createdByUserCorrelationId: user.storeUser.correlationId,
            },
          },
        });
        const err = data?.errors?.[0];
        if (err) {
          failureToast(err.message ?? "Failed to create transaction");
        } else {
          successToast("Transaction recorded");
          setActionModal(null);
          fetchBalance();
          fetchTransactions({ after, before }, currentPage);
        }
      } catch (e) {
        failureToast("Failed to create transaction");
      } finally {
        setCreateLoading(false);
      }
    },
    [
      storeCorrelationId,
      actionModal,
      user?.storeUser?.correlationId,
      axBe,
      after,
      before,
      currentPage,
      fetchBalance,
      fetchTransactions,
    ]
  );

  if (!storeCorrelationId) {
    return (
      <ReportPageLayout title="Cash Drawer">
        <p className="text-gray-500 text-sm">Select a restaurant to view the cash drawer.</p>
      </ReportPageLayout>
    );
  }

  const TABLE_MIN_WIDTH = 920;

  return (
    <ReportPageLayout title="Cash Drawer" fillHeight hideTitle>
      <div
        className="bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden flex-1 flex flex-col min-h-0 max-h-[calc(100vh-5.5rem)]"
        style={{ border: `1px solid ${BORDER_LIGHT}` }}
      >
        {/* Part 1: Toolbar – fixed height, no scroll (match Customers) */}
        <section
          className="shrink-0 py-3.5 px-3 border-b flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between"
          style={{ borderColor: BORDER_LIGHT }}
          aria-label="Cash drawer toolbar"
        >
          <div className="flex items-center gap-2 min-w-0">
            {balanceLoading ? (
              <span className="text-sm text-gray-500">Balance: …</span>
            ) : (
              <span className="text-sm text-gray-700">
                Balance:{" "}
                <span
                  className={
                    currentBalance == null
                      ? "text-gray-600"
                      : (currentBalance ?? 0) >= 0
                        ? "text-emerald-600 font-semibold"
                        : "text-rose-600 font-semibold"
                  }
                >
                  {currentBalance != null ? formatCents(currentBalance) : "—"}
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-block min-w-[150px]">
              <ReportDateRangePicker
                value={datePreset}
                onChange={handleDateRangeChange}
                customFrom={customFrom}
                customTo={customTo}
              />
            </span>
            <div ref={addTransactionRef} className="relative z-30 shrink-0">
            <button
              type="button"
              onClick={() => setAddTransactionOpen((o) => !o)}
              disabled={createLoading}
              className="h-9 px-3 text-sm font-semibold text-white bg-orange rounded-lg shrink-0 transition-[transform,box-shadow] duration-[120ms] ease-out hover:translate-y-[-1px] hover:shadow-[0_8px_20px_rgba(0,0,0,0.10)] active:translate-y-0 active:shadow-[0_4px_12px_rgba(0,0,0,0.08)] disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              Add Transaction
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path d="M5 8l5 5 5-5H5z" />
              </svg>
            </button>
            {addTransactionOpen && (
              <div
                className="absolute right-0 top-full mt-1 z-10 min-w-[160px] py-1 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.08)] overflow-hidden"
                role="menu"
              >
                {ADD_TRANSACTION_OPTIONS.map(({ type, title }) => (
                  <button
                    key={type}
                    type="button"
                    className="w-full text-left px-4 py-2.5 text-sm text-[#828282] hover:bg-[#f1f1f2] hover:text-[#252525]"
                    onClick={() => {
                      setAddTransactionOpen(false);
                      setActionModal({ type, title });
                    }}
                  >
                    {title}
                  </button>
                ))}
              </div>
            )}
            </div>
          </div>
        </section>

        {loading ? (
          <div className="flex justify-center py-12 flex-1 min-h-[120px]">
            <span className="text-gray-500 text-sm">Loading…</span>
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-gray-500 text-sm py-12 text-center flex-1">
            No transactions found
          </p>
        ) : (
          <>
            {/* Part 2: Table – single table with sticky header (aligns when viewport shrinks) */}
            <section
              className="flex-1 min-h-0 flex flex-col min-w-0 relative"
              aria-label="Cash drawer transactions"
              aria-busy={loading}
            >
              <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
                <div className="flex-1 min-h-0 overflow-auto">
                  <table
                    className="w-full text-left border-collapse table-auto"
                    style={{ minWidth: TABLE_MIN_WIDTH }}
                  >
                    <thead>
                      <tr className="text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0]">
                        <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 pl-2 pr-1 align-middle whitespace-nowrap text-left w-10 min-w-[2.25rem]">
                          #
                        </th>
                        <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle text-left min-w-[7.5rem]">
                          Date/Time
                        </th>
                        <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle whitespace-nowrap text-left min-w-[7.5rem]">
                          Type
                        </th>
                        <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle whitespace-nowrap text-left min-w-[5.5rem]">
                          Amount
                        </th>
                        <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle whitespace-nowrap text-left min-w-[6.5rem]">
                          Balance After
                        </th>
                        <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle text-left min-w-[8rem]">
                          Note
                        </th>
                        <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 pl-3 pr-4 align-middle text-left min-w-[7rem]">
                          Created By
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((node, idx) => {
                            const style = CASH_DRAWER_TYPE_STYLES[node.transactionType];
                            const amount = formatAmount(node);
                            const rowNum =
                              totalTransactions === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + idx + 1;
                            return (
                              <tr
                                key={node.correlationId}
                                className="border-b border-[#e5e7eb] hover:bg-[#f8fafc] transition-colors duration-100"
                              >
                                <td className="py-3 pl-2 pr-1 text-sm text-gray-600 tabular-nums text-left align-top">
                                  {rowNum}
                                </td>
                                <td className="py-3 px-3 text-sm align-top">
                                  {node.createdAt != null ? (
                                    <div className="flex flex-col gap-0.5 leading-tight text-[#111827]">
                                      <span className="text-[#6B7280] tabular-nums">
                                        {formatShortTime(node.createdAt)}
                                      </span>
                                      <span className="font-medium tabular-nums">
                                        {formatShortDate(node.createdAt)}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-[#9CA3AF] font-normal">–</span>
                                  )}
                                </td>
                                <td className="py-3 px-3 align-top whitespace-nowrap">
                                  <span
                                    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${style.bg} ${style.text}`}
                                  >
                                    {CASH_DRAWER_TYPE_LABELS[node.transactionType]}
                                  </span>
                                </td>
                                <td
                                  className={`py-3 px-3 text-sm text-left tabular-nums font-semibold align-top whitespace-nowrap ${
                                    amount.positive ? "text-emerald-700" : "text-rose-700"
                                  }`}
                                >
                                  {amount.text}
                                </td>
                                <td className="py-3 px-3 text-sm text-gray-900 text-left tabular-nums align-top whitespace-nowrap">
                                  {formatCents(node.balanceAfter)}
                                </td>
                                <td className="py-3 px-3 text-sm text-gray-600 max-w-[200px] min-w-0 align-top">
                                  <span className="block truncate" title={node.note ?? undefined}>
                                    {node.note ?? "—"}
                                  </span>
                                </td>
                                <td className="py-3 pl-3 pr-4 text-sm text-gray-600 align-top min-w-0">
                                  <span className="block break-words">
                                    {resolveCreatedBy(node.createdByUserCorrelationId, employeeMap)}
                                  </span>
                                </td>
                              </tr>
                            );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Part 3: Pagination – fixed at bottom of card, labels never cut off */}
            <section
              className="shrink-0 py-3.5 px-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t bg-[#fafafa] relative"
              style={{ borderColor: BORDER_LIGHT }}
              aria-label="Pagination"
            >
              <p className="text-sm text-gray-500 order-2 sm:order-1 sm:flex-1 sm:min-w-0 sm:text-left shrink-0">
                {totalTransactions > 0
                  ? `Showing ${start}–${end} of ${totalTransactions}`
                  : ""}
              </p>
              <div className="flex items-center justify-center gap-2 flex-wrap order-1 sm:order-2 sm:flex-none sm:absolute sm:left-1/2 sm:-translate-x-1/2 shrink-0">
                <button
                  type="button"
                  className="h-9 min-w-[4.5rem] px-3 flex items-center justify-center text-sm font-medium text-[#252525] bg-white border border-[#F1F1F2] rounded-lg disabled:opacity-50 disabled:cursor-default focus:outline-none hover:bg-[#fafafa] whitespace-nowrap"
                  onClick={loadPrevPage}
                  disabled={currentPage <= 1 || loading}
                >
                  Previous
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {pageNumbers.map((item, idx) =>
                    item === "ellipsis" ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-sm shrink-0">
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`min-w-[36px] w-9 h-9 rounded-lg text-sm font-medium flex items-center justify-center focus:outline-none shrink-0 ${
                          currentPage === item
                            ? "bg-orange text-white"
                            : "bg-gray-50 text-gray-700"
                        } disabled:opacity-50 disabled:cursor-default`}
                        onClick={() => goToPage(item)}
                        disabled={!canGoToPage(item)}
                      >
                        {item}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  className="h-9 min-w-[4.5rem] px-3 flex items-center justify-center text-sm font-medium text-[#252525] bg-white border border-[#F1F1F2] rounded-lg disabled:opacity-50 disabled:cursor-default focus:outline-none hover:bg-[#fafafa] whitespace-nowrap"
                  onClick={loadNextPage}
                  disabled={currentPage >= totalPages || loading}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:block flex-1 min-w-0 order-3" aria-hidden="true" />
            </section>
          </>
        )}
      </div>

      {actionModal && (
        <CashDrawerActionModal
          title={actionModal.title}
          type={actionModal.type}
          onConfirm={handleCreateTransaction}
          onCancel={() => setActionModal(null)}
        />
      )}
    </ReportPageLayout>
  );
}
